/**
 * PORT / STBD filtering for performance aggregate rows.
 *
 * Tack is not stored on aggregates - it is derived from the sign of TWA, the same way
 * charts derive it (AdvancedScatter.filterData / enhancedScatterWorkerManager): negative
 * TWA is PORT, positive TWA is STBD. TWA is read from the `Twa_deg_avg` metadata field
 * (present on AVG/STD/AAV rows) with a fallback to the project default TWA channel, so
 * the filter agrees with the page's upwind/downwind filter.
 */

import { defaultChannelsStore } from "../store/defaultChannelsStore";

export type PerformanceTack = 'PORT' | 'STBD' | null;

/** TWA for an aggregate row, or null when no usable TWA value exists. */
export function performanceRowTwa(row: Record<string, unknown> | null | undefined): number | null {
  if (!row) {return null;}

  const twaAvg = row.Twa_deg_avg ?? row.twa_deg_avg;
  if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
    return Number(twaAvg);
  }

  const twaField = defaultChannelsStore.twaName();
  const twaValue = row[twaField] ?? row[twaField.toLowerCase()] ?? row.twa;
  if (twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue))) {
    return Number(twaValue);
  }

  return null;
}

/** True when the row passes the tack filter. A null filter passes everything. */
export function passesPerformanceTackFilter(
  row: Record<string, unknown> | null | undefined,
  tack: PerformanceTack
): boolean {
  if (tack !== 'PORT' && tack !== 'STBD') {return true;}

  const twa = performanceRowTwa(row);
  if (twa === null) {return false;}

  return tack === 'PORT' ? twa < 0 : twa >= 0;
}
